import { initializePDF } from "../hooks/pdfHandlers";
import {
  PDFPortraitScheduleConstants,
  PDFLandscapeScheduleConstants,
} from "../static/pdfConstants";
import {
  buildScheduleCompanyHeader,
  buildNotes,
  buildCalculatorNotes,
} from "./page";
import { BaseExcelGenerator } from "../excel/BaseExcelGenerator";
import logoBlack from "../../theme/logo/logo_black.png";
import { getSubscriptionStatus } from "./subscriptionStatus";

export class BasePdfGenerator {
  static _sharedExcel = null;
  static _sharedExcelConfig = { autoDownload: true };

  static setSharedExcel(excelGenerator, config = {}) {
    BasePdfGenerator._sharedExcel = excelGenerator || null;
    BasePdfGenerator._sharedExcelConfig = {
      autoDownload: true,
      ...config,
    };
  }

  static getSharedExcel() {
    return BasePdfGenerator._sharedExcel;
  }

  static clearSharedExcel() {
    BasePdfGenerator._sharedExcel = null;
    BasePdfGenerator._sharedExcelConfig = { autoDownload: true };
  }

  constructor(isLandscape = false, isExcel = false) {
    this.isExcel = isExcel;
    this.subscriptionStatus = null; // Will be set via setSubscriptionStatus

    if (isExcel) {
      // Use Excel generator internally
      this.excel = BasePdfGenerator._sharedExcel || new BaseExcelGenerator();
      this._excelIsShared = Boolean(BasePdfGenerator._sharedExcel);
      this.isLandscape = false; // Excel doesn't use landscape concept
      this.fs = 10;
    } else {
      // Use PDF generator
      this.doc = initializePDF(isLandscape ? "landscape" : undefined);
      this.isLandscape = isLandscape;
      this.constants = isLandscape
        ? PDFLandscapeScheduleConstants
        : PDFPortraitScheduleConstants;
      this.leftX = this.constants.leftX;
      this.rightX = this.constants.rightX;
      this.pageHeight = this.doc.internal.pageSize.height;
      this.lineHeight = this.constants.lineHeight;
      this.initY = this.constants.initY;
      this.refreshY = this.initY;
      this.startY = this.initY;
      this.amountX = this.constants.amountX;
      this.maxSchUnderlineWidth = this.constants.maxSchUnderlineWidth;
      this.fs = 10;
    }
  }

  setSubscriptionStatus(status) {
    this.subscriptionStatus = status;
  }

  addWatermark() {
    if (this.isExcel) return; // No watermark for Excel

    if (process.env.REACT_APP_RENDER_ENV !== true) return;

    // Get subscription status (from setter or global store)
    const status = this.subscriptionStatus || getSubscriptionStatus();

    // Check if subscription is not active
    if (status === "active") return;

    try {
      const pageWidth = this.doc.internal.pageSize.width;
      const pageHeight = this.doc.internal.pageSize.height;

      // Center position
      const centerX = pageWidth / 2;
      const centerY = pageHeight / 2;

      // Save current graphics state
      this.doc.saveGraphicsState();

      // Set global alpha for transparency (0.0 to 1.0, lower = more transparent)
      this.doc.setGState(
        this.doc.GState({
          opacity: 0.12, // 30% opacity for watermark effect
        })
      );

      // Add logo as watermark at center
      // Assuming logo is approximately 100x100px, adjust as needed
      const logoWidth = 192;
      const logoHeight = 35.3;
      const angle = (-40 * Math.PI) / 180;

      // vector from top-left to center
      const cx = logoWidth / 2;
      const cy = logoHeight / 2;

      // rotate that vector
      const rx = cx * Math.cos(angle) - cy * Math.sin(angle);
      const ry = cx * Math.sin(angle) + 10 * cy * Math.cos(angle);

      // compensated top-left
      const x = centerX - rx;
      const y = centerY - ry;

      this.doc.addImage(
        logoBlack,
        "PNG",
        x,
        y,
        logoWidth,
        logoHeight,
        undefined,
        undefined,
        -40
      );

      // Restore graphics state
      this.doc.restoreGraphicsState();
    } catch (error) {
      console.warn("Failed to add watermark:", error);
    }
  }

  initializeHeader(company, schedule) {
    if (this.isExcel) {
      this.excel.initializeHeader(
        company,
        schedule,
        schedule?.name || "Schedule"
      );
    } else {
      this.startY = buildScheduleCompanyHeader(
        company,
        schedule,
        this.doc,
        this.leftX,
        this.rightX,
        this.startY,
        this.fs
      );
    }
  }

  buildNotes(notes, reviewNotes, forView = true, data = [], company) {
    if (this.isExcel) {
      this.excel.buildNotes(notes, reviewNotes, forView, data, company);
    } else {
      // const processedNotes = buildNotesWithCurrencyRate(company, notes);
      const processedReviewNotes = buildCalculatorNotes(reviewNotes, data);

      if (this.startY)
        buildNotes(
          notes,
          processedReviewNotes,
          this.doc,
          this.startY,
          this.initY,
          this.leftX,
          this.rightX,
          this.lineHeight,
          this.pageHeight,
          this.refreshY,
          forView,
          true
        );
    }
  }

  getDoc(forView = false, company = null, schedule = null) {
    if (this.isExcel) {
      const shouldAutoDownload =
        !this._excelIsShared || BasePdfGenerator._sharedExcelConfig.autoDownload;

      if (shouldAutoDownload) {
        return this.getExcel().download(
          forView,
          company?.name,
          schedule?.name || schedule?.type
        );
      }

      // For shared-workbook mode: caller will download once at the end
      return this.getExcel();
    }
    return this.doc;
  }

  getExcel() {
    if (this.isExcel) {
      return this.excel;
    }
    return null;
  }
}
